import type {
  Annotation,
  AttachmentDetail,
  ByoProvider,
  Case,
  CaseGroup,
  CaseLink,
  CaseRecord,
  CaseStatus,
  CaseVolume,
  ChatMessage,
  Contact,
  CostsSummary,
  Direction,
  ExtractionDropFlag,
  FileUploadResult,
  FindingAction,
  KeyFact,
  ReceiptData,
  RecordFact,
  RecordVersion,
  Share,
  Significance,
  SourceType,
  TimelineRecord,
  ValidationResult,
  VolumeSummaryPayload,
} from "./types";
import { getSettings } from "./lib/settings";
import { drainSseEvents } from "./lib/sse";

// In production the frontend and API sit on sibling subdomains of casethread.app,
// so we use a same-site absolute URL. In dev Vite proxies /api to localhost:8787.
// `import.meta.env` is undefined when this module is bundled for the
// non-Vite layout test harness (esbuild CJS) — optional-chain so importing
// `api` from a component under test never throws at module load.
const API_BASE =
  import.meta.env?.VITE_API_BASE ??
  (import.meta.env?.DEV ? "" : "https://api.casethread.app");

// Token provider pattern — the Clerk-aware <ClerkTokenProvider> calls
// setTokenProvider() on mount with a getToken() that resolves to the
// current Clerk JWT (or null, pre-sign-in). Decoupling the token source
// from this module means api.ts stays framework-agnostic and unit-test
// friendly, and we don't have to thread a hook through every call site.
//
// When no provider is registered (e.g. public landing page paths that
// haven't mounted ClerkTokenProvider yet, or a misconfigured build with
// no Clerk key), we send requests with no Authorization header — the
// Worker will answer 401, which the in-request handler converts to a
// /welcome redirect.
let _getToken: (() => Promise<string | null>) | null = null;

export function setTokenProvider(fn: () => Promise<string | null>): void {
  _getToken = fn;
}

// AI preference headers — sent on every request. The Worker uses them to
// decide Claude vs. Ollama and to enforce Privacy mode. Pulled from the
// settings store, which also drives the UI.
function aiPrefHeaders(): Record<string, string> {
  try {
    const s = getSettings();
    return {
      "X-Ai-Provider": s.ai.provider,
      "X-Ai-Privacy": s.ai.privacy ? "1" : "0",
      "X-Ai-Style": s.interrogate.responseStyle,
      "X-Ai-Language": s.interrogate.language,
    };
  } catch {
    return {};
  }
}

// Exposed so the ZIP exporter can attach the bearer token when fetching
// attachment blobs directly from /images. Async now that the token is
// resolved asynchronously via the Clerk provider.
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = _getToken ? await _getToken() : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = _getToken ? await _getToken() : null;
  const authHeader: Record<string, string> = token
    ? { Authorization: `Bearer ${token}` }
    : {};
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: {
      ...(init.body && !(init.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...authHeader,
      ...aiPrefHeaders(),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401) {
    // No (or invalid) Clerk session — send the user to the marketing /
    // sign-in page. location.href rather than SPA nav because the app
    // shell may already be rendering and we want a clean reset.
    //
    // Guard against an infinite-loop edge case: if the user IS signed
    // in with Clerk but the Worker rejects the token (e.g. JWT
    // template misconfigured so `email` claim is missing), a naive
    // redirect to /welcome would land on <SignedIn>→AuthenticatedApp
    // (since Clerk still thinks they're signed in), fire api.me()
    // again, 401 again, redirect again, forever. Skipping the
    // redirect when we're already there lets the error bubble to the
    // UI instead so the user can sign out from the sidebar and
    // recover.
    if (window.location.pathname !== "/welcome") {
      window.location.href = "/welcome";
    }
    throw new Error("Unauthorized");
  }
  if (res.status === 402) {
    // Payment required — plan-guard rejected an AI call. Unlike 401,
    // this does NOT redirect. The caller catches the error and its
    // `code` property (trial_expired | subscription_required) so the
    // UI can render an inline upgrade prompt rather than kicking the
    // user out of the page they're on.
    let body: { error?: string; message?: string } = {};
    try {
      body = (await res.json()) as typeof body;
    } catch {}
    const err = new Error(body.message ?? "Payment required");
    (err as Error & { code?: string }).code = body.error;
    throw err;
  }
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    let details: Record<string, unknown> = {};
    try {
      const body = (await res.json()) as { error?: string } & Record<
        string,
        unknown
      >;
      details = body;
      if (body.error) msg = body.error;
    } catch {}
    // Attach the parsed error body so callers that need structured detail
    // (e.g. the Focused 413 "too_large" size: est_tokens/matched_count) can
    // read it; existing callers that only read .message are unaffected.
    const err = new Error(msg) as Error & { details?: Record<string, unknown> };
    err.details = details;
    throw err;
  }
  return res.json() as Promise<T>;
}

export interface ServerProfile {
  id: string;
  email: string;
  display_name: string | null;
  retention: "never" | "1y" | "3y";
  notify_on_forward: boolean;
  notify_weekly_summary: boolean;
  // Subscription status, piggybacked onto /api/me so the trial banner
  // can render without a second round-trip. See /api/billing/status
  // for an explicit refresh after checkout return.
  plan: string;
  trial_ends_at: string | null;
  days_remaining: number | null;
  has_subscription: boolean;
  // True when the user has cancelled but their paid period hasn't
  // ended yet — Stripe keeps the subscription 'active' with this
  // flag set until current_period_end.
  cancel_at_period_end: boolean;
  current_period_end: string | null;
}

export interface ProfilePatch {
  display_name?: string;
  retention?: "never" | "1y" | "3y";
  notify_on_forward?: boolean;
  notify_weekly_summary?: boolean;
}

// 401/402 handling shared with request() for the streaming extract path —
// a streamed fetch bypasses request(), so it must enforce the same auth guards.
async function throwIfAuthError(res: Response): Promise<void> {
  if (res.status === 401) {
    if (window.location.pathname !== "/welcome") {
      window.location.href = "/welcome";
    }
    throw new Error("Unauthorized");
  }
  if (res.status === 402) {
    let body: { error?: string; message?: string } = {};
    try {
      body = (await res.json()) as typeof body;
    } catch {}
    const err = new Error(body.message ?? "Payment required");
    (err as Error & { code?: string }).code = body.error;
    throw err;
  }
}

interface ExtractChunkDone {
  processed: number;
  total: number;
  next_start: number;
  done: boolean;
  stored: number;
  failed: string[];
  flagged?: ExtractionDropFlag[];
}

// Drive ONE chunk over SSE: the server streams a `progress` event after each
// record (per-record liveness) and a terminal `done` event with the chunk
// result. onRecord fires once per record so the caller can advance a counter
// and stamp a heartbeat; the resolved value is the `done` payload.
async function streamExtractChunk(
  caseId: string,
  chunkStart: number,
  chunkSize: number,
  onRecord: (completed: number, total: number) => void,
): Promise<ExtractChunkDone> {
  const token = _getToken ? await _getToken() : null;
  const res = await fetch(`${API_BASE}/api/cases/${caseId}/extract-all-facts`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...aiPrefHeaders(),
    },
    body: JSON.stringify({ chunk_start: chunkStart, chunk_size: chunkSize }),
  });
  await throwIfAuthError(res);
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let donePayload: ExtractChunkDone | null = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const drained = drainSseEvents(buffer);
    buffer = drained.rest;
    for (const evt of drained.events) {
      if (evt.event === "progress") {
        const d = JSON.parse(evt.data) as { completed: number; total: number };
        onRecord(d.completed, d.total);
      } else if (evt.event === "done") {
        donePayload = JSON.parse(evt.data) as ExtractChunkDone;
      }
    }
  }
  if (!donePayload) {
    throw new Error("extraction stream ended without a 'done' event");
  }
  return donePayload;
}

export interface SearchResult {
  record_id: string;
  kind: "record" | "attachment";
  attachment_id: string | null;
  attachment_filename: string | null;
  // Snippet text with the matched span wrapped in U+0002 … U+0003 control
  // chars (NOT HTML) — render by splitting on them, never via innerHTML.
  snippet: string;
  trust: "high" | "low";
}

// One retrieved record in Focused Answer's deterministic preview. Extends
// SearchResult with the date/subject shown on the review chips.
export interface FocusedRecord extends SearchResult {
  date: string;
  subject: string | null;
  // Deterministic content-derived descriptor (migration 0055). Shown as the
  // primary line in the matched list, with `subject` as the tooltip; null when
  // the record has no facts to summarize.
  content_label: string | null;
  // How many matched thread members this record stood in for (1 = not a
  // collapse). Pinned + forwarded on follow-ups so the "latest message in a
  // thread of N" note keeps working when the answer bypasses re-retrieval.
  collapse_count: number;
}

// One interrogation group: a contiguous chronological slice of the matched
// set that fits in a single AI pass. The user interrogates one group at a
// time; `record_ids` is the pinned set passed to /focused/answer.
export interface FocusedBatch {
  record_ids: string[];
  count: number;
  est_tokens: number;
  from_date: string;
  to_date: string;
}

export interface FocusedPrepareResult {
  // The concept groups that will be searched — editable by the user before
  // running. Within a group the terms are OR'd (variants of one concept);
  // across groups they are AND'd (a record must match every concept).
  // Reproducible: the server owns synonym expansion, so the same phrase
  // always yields the same group.
  groups: string[][];
  // The FULL matched set (chronological), always returned regardless of
  // count — listing is free and never blocked by the AI budget.
  records: FocusedRecord[];
  // The matched set packed into fitting chronological batches. One batch =
  // fits in a single AI pass. >1 batch = interrogate one group at a time.
  batches: FocusedBatch[];
  // Records after collapsing quoted email threads to their latest message.
  matched_count: number;
  // Records that matched BEFORE thread-collapse — shown alongside
  // matched_count so the dedup ("N matched → M after collapsing") is
  // visible and inspectable.
  raw_count: number;
  est_tokens: number;
  // True when the whole matched set fits in one AI pass (batches.length ≤ 1).
  fits: boolean;
  // Question words the deterministic INTENT stoplist held OUT of retrieval,
  // each with the number of case records that contain it. Shown in a subdued
  // "Not searched" row; one click adds any back into a concept. Only populated
  // on the phrase path — chip-edit re-previews return [] (client keeps its
  // own row). Empty for a topic-only phrase.
  excluded: Array<{ term: string; count: number }>;
  // Total records in the case (denominator for the breadth signal).
  total_records: number;
  // Matched set covers > 50% of the case → the search may be too broad.
  broad: boolean;
  // Per-chip match counts (term → number of case records containing it), so an
  // over-broad synonym chip is self-evident and one click to remove. Refreshed
  // on every chip edit. Case-scoped, deterministic — same source as `excluded`.
  chip_counts: Record<string, number>;
  // Set when a phrase was entirely question words (no topic to search) — an
  // actionable nudge, not an error.
  message?: string;
  budget: number;
}

export interface FocusedAnswerResult {
  reply: string;
  provider: "anthropic" | "ollama";
  model: string;
  matched_count: number;
  est_tokens: number;
  // Oldest conversation turns dropped to stay within the fit budget (the
  // records are never trimmed). 0 in the common case.
  trimmed_turns: number;
}

// One structured finding from a group's map pass (Step 2). thread_ids drive
// deterministic cross-group request↔response pairing at reduce time.
export interface FocusedFinding {
  kind: "request" | "response" | "event";
  date: string;
  party: string;
  topic: string;
  short_quote: string;
  rec_ids: string[];
  thread_ids: string[];
}

// Per-record gate results for a cited [rec:id] chip in a spanning answer.
export interface CitationVerification {
  citation_ok: boolean;
  quote_ok: boolean;
  date_ok: boolean;
}

export interface FocusedMapResult {
  findings: FocusedFinding[];
  // rec_ids the model cited that weren't in this group — surfaced, never kept.
  flagged: string[];
  // short_quotes that couldn't be verified verbatim against the source.
  unverified_quotes: string[];
  // finding dates not grounded in the cited record (date gate) — surfaced.
  unverified_dates: string[];
  // records backing a finding whose quote / date failed its gate (per-citation
  // marks roll these up across groups).
  unverified_quote_rec_ids: string[];
  unverified_date_rec_ids: string[];
  // findings JSON was cut off by the token limit — findings below are missing.
  truncated: boolean;
  matched_count: number;
  no_findings: boolean;
  provider: "anthropic" | "ollama";
  model: string;
}

export interface FocusedReduceResult {
  reply: string;
  provider: "anthropic" | "ollama";
  model: string;
  // Citations in the spanning answer absent from any finding — surfaced.
  unverified_citations: string[];
  // Finding quotes that couldn't be verified verbatim (aggregate) — surfaced.
  unverified_quotes: number;
  // Finding dates that failed the date gate (aggregate) — surfaced.
  unverified_dates: number;
  // Per-record gate results keyed by rec_id, for the answer's citation chips.
  verification: Record<string, CitationVerification>;
  // The answer was cut off by a length limit — entries may be missing below.
  truncated: boolean;
  failed_groups: number[];
  created_at: string;
}

// The persisted most-recent cross-group answer for a case (restore on load).
export interface StoredReduceResult {
  question: string;
  answer: string;
  groups: Array<{
    group_index: number;
    from_date: string;
    to_date: string;
    failed: boolean;
  }>;
  unverified: {
    citations: string[];
    quotes: number;
    dates: number;
    verification?: Record<string, CitationVerification>;
  };
  group_findings: Array<{
    group_index: number;
    from_date: string;
    to_date: string;
    findings: FocusedFinding[];
    failed: boolean;
  }>;
  // The persisted answer was cut off by a length limit — restore the warning.
  truncated: boolean;
  // Search identity: fingerprint of the matched set the answer spanned (NULL for
  // legacy rows) + its record count, for the previous-vs-current provenance check.
  fingerprint: string | null;
  matched_count: number;
  created_at: string;
}

export const api = {
  me: () =>
    request<{ user: ServerProfile }>("/api/me"),

  // Deterministic full-text search over record + attachment content for one
  // case. Returns ranked results (best first), one per record.
  searchRecords: (caseId: string, q: string) =>
    request<{ results: SearchResult[] }>(
      `/api/cases/${caseId}/search?q=${encodeURIComponent(q)}`,
    ),

  updateProfile: (patch: ProfilePatch) =>
    request<{ user: ServerProfile }>("/api/me", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deleteAccount: () =>
    request<{ ok: true; deleted_files: number }>("/api/me", {
      method: "DELETE",
    }),

  listCases: () => request<{ cases: Case[] }>("/api/cases"),

  listGroups: () =>
    request<{ groups: CaseGroup[] }>("/api/cases/groups"),

  createCase: (
    name: string,
    description: string,
    slug?: string,
    groupName?: string | null,
  ) =>
    request<{ case: Case }>("/api/cases", {
      method: "POST",
      body: JSON.stringify({
        name,
        description,
        slug,
        group_name: groupName ?? null,
      }),
    }),

  checkSlug: (slug: string) =>
    request<{
      available: boolean;
      normalised: string;
      full_address?: string;
      error?: string;
    }>(`/api/cases/check-slug?slug=${encodeURIComponent(slug)}`),

  updateCaseSlug: (caseId: string, slug: string) =>
    request<{ case: Case }>(`/api/cases/${caseId}/slug`, {
      method: "PATCH",
      body: JSON.stringify({ slug }),
    }),

  // Readable email-slug (the "uv" in "uv@casethread.app").
  // checkEmailSlug is used by the slug editor for live availability
  // feedback; updateCaseEmailSlug commits the rename and returns the
  // updated case row. Old slugs are retained as aliases by the
  // server, so in-flight mail to the previous address keeps routing.
  checkEmailSlug: (slug: string) =>
    request<{
      available: boolean;
      normalised: string;
      full_address?: string;
      error?: string;
    }>(`/api/cases/check-email-slug?slug=${encodeURIComponent(slug)}`),

  updateCaseEmailSlug: (caseId: string, slug: string) =>
    request<{ case: Case }>(`/api/cases/${caseId}/email-slug`, {
      method: "PATCH",
      body: JSON.stringify({ slug }),
    }),

  // Fact-based timeline (Phase 2). A single deterministic read assembled
  // from records + verified facts on the server — no AI, no generation
  // step, no cache. Returns one entry per record in chronological order.
  getTimeline: (caseId: string) =>
    request<{ timeline: TimelineRecord[] }>(
      `/api/cases/${caseId}/timeline`,
    ),

  // Scan every record in the case, run the deterministic date
  // extractor on the body/OCR text, and return the records whose
  // stored record_date disagrees with the extracted date by more
  // than a day. No records are modified — this is advisory.
  repairDatesScan: (caseId: string) =>
    request<{
      suggestions: Array<{
        record_id: string;
        current_date: string;
        suggested_date: string;
        subject: string | null;
        preview: string;
        file_r2_key: string | null;
        file_mime_type: string | null;
        confidence: "normal" | "unlikely";
        confidence_reason: string | null;
      }>;
      scanned: number;
    }>(`/api/cases/${caseId}/repair-dates`, { method: "POST" }),

  applyDateRepairs: (
    caseId: string,
    repairs: Array<{ record_id: string; new_date: string }>,
  ) =>
    request<{ applied: number }>(
      `/api/cases/${caseId}/apply-date-repairs`,
      {
        method: "POST",
        body: JSON.stringify({ repairs }),
      },
    ),

  getCase: (id: string) => request<{ case: Case }>(`/api/cases/${id}`),

  deleteCase: (id: string) =>
    request<{ ok: true }>(`/api/cases/${id}`, { method: "DELETE" }),

  updateCase: (
    id: string,
    patch: Partial<{
      name: string;
      description: string | null;
      status: CaseStatus;
      deadline: string | null;
      group_name: string | null;
    }>,
  ) =>
    request<{ case: Case }>(`/api/cases/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  // --- Contacts ---
  listContacts: (caseId: string) =>
    request<{ contacts: Contact[] }>(`/api/cases/${caseId}/contacts`),

  createContact: (
    caseId: string,
    body: {
      name: string;
      role?: string | null;
      email?: string | null;
      phone?: string | null;
      notes?: string | null;
    },
  ) =>
    request<{ contact: Contact }>(`/api/cases/${caseId}/contacts`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateContact: (
    id: string,
    body: Partial<{
      name: string;
      role: string | null;
      email: string | null;
      phone: string | null;
      notes: string | null;
    }>,
  ) =>
    request<{ contact: Contact }>(`/api/contacts/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteContact: (id: string) =>
    request<{ ok: true }>(`/api/contacts/${id}`, { method: "DELETE" }),

  // --- Linked cases ---
  listLinks: (caseId: string) =>
    request<{ links: CaseLink[] }>(`/api/cases/${caseId}/links`),

  createLink: (caseId: string, linkedCaseId: string, note?: string) =>
    request<{ link: unknown }>(`/api/cases/${caseId}/links`, {
      method: "POST",
      body: JSON.stringify({ linked_case_id: linkedCaseId, note }),
    }),

  deleteLink: (linkId: string) =>
    request<{ ok: true }>(`/api/links/${linkId}`, { method: "DELETE" }),

  // --- Shares ---
  listShares: (caseId: string) =>
    request<{ shares: Share[] }>(`/api/cases/${caseId}/shares`),

  createShare: (caseId: string) =>
    request<{ share: Share }>(`/api/cases/${caseId}/shares`, {
      method: "POST",
    }),

  revokeShare: (shareId: string) =>
    request<{ ok: true }>(`/api/shares/${shareId}`, { method: "DELETE" }),

  // --- Annotations ---
  listAnnotations: (recordId: string) =>
    request<{ annotations: Annotation[] }>(
      `/api/records/${recordId}/annotations`,
    ),

  createAnnotation: (
    recordId: string,
    selectedText: string,
    note: string,
  ) =>
    request<{ annotation: Annotation }>(
      `/api/records/${recordId}/annotations`,
      {
        method: "POST",
        body: JSON.stringify({ selected_text: selectedText, note }),
      },
    ),

  deleteAnnotation: (id: string) =>
    request<{ ok: true }>(`/api/annotations/${id}`, { method: "DELETE" }),

  // --- Record updates (pin toggle + edits) ---
  updateRecord: (
    id: string,
    body: Partial<{
      pinned: boolean;
      subject: string | null;
      body_text: string | null;
      notes: string | null;
      is_duplicate: boolean;
    }>,
  ) =>
    request<{ record: CaseRecord }>(`/api/records/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  // Retroactive duplicate scan for a whole case. Removes true content
  // duplicates (Jaccard >= 90%) and returns how many were removed.
  deduplicate: (caseId: string) =>
    request<{
      scanned: number;
      removed: number;
    }>(`/api/cases/${caseId}/deduplicate`, { method: "POST" }),

  rethread: (caseId: string) =>
    request<{
      scanned: number;
      threads: number;
      assignments: number;
      orphans_cleared: number;
    }>(`/api/cases/${caseId}/rethread`, { method: "POST" }),

  unthread: (caseId: string) =>
    request<{ cleared: number }>(
      `/api/cases/${caseId}/unthread`,
      { method: "POST" },
    ),

  // Re-scores a whole case. CHUNKED + client-driven (mirrors extractAllFacts):
  // each /score call scores ≤20 records (one Worker invocation, ~41
  // subrequests — safely under the ~50 cap) and returns where to resume. A
  // single unbounded call blew the cap on large cases ("Too many subrequests"),
  // failing every record past ~24. Loop the chunks, accumulate the totals.
  scoreAll: async (
    caseId: string,
    onProgress?: (p: { completed: number; total: number }) => void,
  ): Promise<{ scanned: number; scored: number; failed: number }> => {
    const CHUNK = 20;
    let start = 0;
    let total = 0;
    let scored = 0;
    let failed = 0;
    // Guard against a runaway loop; 100 chunks × 20 = 2000 records max.
    for (let guard = 0; guard < 100; guard++) {
      const r = await request<{
        scanned: number;
        scored: number;
        failed: number;
        hashed: number;
        total: number;
        next_start: number;
        done: boolean;
      }>(`/api/cases/${caseId}/score`, {
        method: "POST",
        body: JSON.stringify({ chunk_start: start, chunk_size: CHUNK }),
      });
      total = r.total;
      scored += r.scored;
      failed += r.failed;
      onProgress?.({ completed: r.next_start, total });
      if (r.done) break;
      start = r.next_start;
      // Pace between chunks (same cadence as extraction).
      await new Promise<void>((res) => setTimeout(res, 2000));
    }
    return { scanned: total, scored, failed };
  },

  rerunOcr: (recordId: string) =>
    request<{
      record: CaseRecord;
      ocr_result: {
        body_chars: number;
        ocr_chars: number;
        extracted_empty: boolean;
      };
    }>(`/api/records/${recordId}/reocr`, {
      method: "POST",
    }),

  rerunTranscribe: (recordId: string) =>
    request<{
      record: CaseRecord;
      transcript_result: {
        body_chars: number;
        extracted_empty: boolean;
      };
    }>(`/api/records/${recordId}/retranscribe`, {
      method: "POST",
    }),

  getKeyFacts: (caseId: string) =>
    request<{ key_facts: KeyFact[]; updated_at: string | null }>(
      `/api/cases/${caseId}/key-facts`,
    ),

  refreshKeyFacts: (caseId: string) =>
    request<{ key_facts: KeyFact[]; updated_at: string | null }>(
      `/api/cases/${caseId}/key-facts/refresh`,
      { method: "POST" },
    ),

  // Cached costs summary ({ costs: null } before first scan).
  getCosts: (caseId: string) =>
    request<{
      costs: CostsSummary | null;
      validation?: ValidationResult | null;
    }>(`/api/cases/${caseId}/costs`),

  // Forces a fresh AI scan over all records and replaces the cache.
  regenerateCosts: (caseId: string) =>
    request<{ costs: CostsSummary; validation?: ValidationResult | null }>(
      `/api/cases/${caseId}/costs/regenerate`,
      { method: "POST" },
    ),

  updateTags: (recordId: string, tags: string[]) =>
    request<{ record: CaseRecord }>(`/api/records/${recordId}`, {
      method: "PATCH",
      body: JSON.stringify({ tags: tags.join(",") }),
    }),

  overrideSignificance: (recordId: string, significance: Significance) =>
    request<{ record: CaseRecord }>(`/api/records/${recordId}`, {
      method: "PATCH",
      body: JSON.stringify({ significance }),
    }),

  // Document-kind category: set a user override (wins over the deterministic
  // auto value), clear it (revert to auto), or run the opt-in batched AI
  // classifier over an ambiguous set (returns SUGGESTIONS to confirm).
  setRecordCategory: (caseId: string, recordId: string, bucket: string) =>
    request<{ bucket: string }>(
      `/api/cases/${caseId}/records/${recordId}/category`,
      { method: "PUT", body: JSON.stringify({ bucket }) },
    ),

  clearRecordCategory: (caseId: string, recordId: string) =>
    request<{ bucket: string }>(
      `/api/cases/${caseId}/records/${recordId}/category`,
      { method: "DELETE" },
    ),

  classifyRecords: (caseId: string, recordIds: string[]) =>
    request<{ suggestions: { id: string; bucket: string }[] }>(
      `/api/cases/${caseId}/records/classify`,
      { method: "POST", body: JSON.stringify({ recordIds }) },
    ),

  listVersions: (recordId: string) =>
    request<{ source_hash: string | null; versions: RecordVersion[] }>(
      `/api/records/${recordId}/versions`,
    ),

  restoreVersion: (recordId: string, versionNumber: number) =>
    request<{ record: CaseRecord }>(
      `/api/records/${recordId}/versions/restore`,
      {
        method: "POST",
        body: JSON.stringify({ version_number: versionNumber }),
      },
    ),

  updateRecordContent: (
    recordId: string,
    content: string,
    reason?: string,
  ) =>
    request<{ record: CaseRecord; changed: boolean }>(
      `/api/records/${recordId}/content`,
      {
        method: "PATCH",
        body: JSON.stringify({ content, reason }),
      },
    ),

  listRecords: (caseId: string) =>
    request<{ records: CaseRecord[] }>(`/api/cases/${caseId}/records`),

  createRecord: (
    caseId: string,
    body: {
      record_date: string;
      direction: Direction;
      source_type: SourceType;
      from_addr?: string | null;
      to_addr?: string | null;
      subject?: string | null;
      body_text?: string | null;
      notes?: string | null;
      ocr_text?: string | null;
      file_r2_key?: string | null;
      file_name?: string | null;
      file_mime_type?: string | null;
      // Receipt-flow fields — when the upload endpoint detected a
      // receipt, the caller forwards the structured JSON here so the
      // backend can persist it and auto-tag the record.
      receipt_data?: ReceiptData | null;
      tags?: string | null;
      // Per-field extraction confidence (see migration 0035). Set by
      // upload / OCR callers based on what the server returned in
      // FileUploadResult.extracted.extraction_confidence. Stored
      // verbatim as a JSON string on the records row.
      extraction_confidence?: object | string | null;
      // Tags how the record originated. File-upload + paste clients
      // leave this unset and the server defaults to "upload". The
      // Write-record modal passes "manual" so the UI can render the
      // pen icon instead of the file/email icon.
      creation_source?: "upload" | "email" | "manual";
      // Provenance of record_date. Clients pass what they know:
      // the file-upload path forwards whatever the server returned
      // in FileUploadResult.extracted.date_source; paste-canvas /
      // fresh-text records default to "upload"; Write-record uses
      // "manual". Stored as-is on the row (see migration 0037).
      date_source?:
        | "header"
        | "forwarded_header"
        | "body"
        | "upload"
        | "manual";
    },
  ) =>
    // A true content duplicate (same content already in the case) is dropped
    // silently by the server: it returns { duplicate: true } and no record.
    request<{
      record?: CaseRecord;
      duplicate?: boolean;
      duplicate_of?: string;
    }>(`/api/cases/${caseId}/records`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  deleteRecord: (id: string) =>
    request<{ ok: true }>(`/api/records/${id}`, { method: "DELETE" }),

  // Lazy-loaded attachment detail — the list endpoint omits the extracted
  // text blob so the first paint stays light.
  getAttachment: (id: string) =>
    request<{ attachment: AttachmentDetail }>(`/api/attachments/${id}`),

  // Delete a single attachment (used by the Files tab). Removes the
  // attachments row and the R2 object; leaves the parent record alone.
  deleteAttachment: (id: string) =>
    request<{ ok: true }>(`/api/attachments/${id}`, { method: "DELETE" }),

  // Clear the primary file off a legacy record (records.file_r2_key)
  // while keeping the record itself. Mirrors deleteAttachment for files
  // that aren't in the attachments table.
  clearRecordFile: (id: string) =>
    request<{ record: CaseRecord }>(`/api/records/${id}/file`, {
      method: "DELETE",
    }),

  renameAttachment: (id: string, filename: string) =>
    request<{ attachment: AttachmentDetail }>(`/api/attachments/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ filename }),
    }),

  renameRecordFile: (recordId: string, fileName: string) =>
    request<{ record: CaseRecord }>(
      `/api/records/${recordId}/file-name`,
      {
        method: "PATCH",
        body: JSON.stringify({ file_name: fileName }),
      },
    ),

  backfillFileSizes: (caseId: string, r2Keys: string[]) =>
    request<{ sizes: Record<string, number> }>(
      `/api/cases/${caseId}/files/backfill-sizes`,
      {
        method: "POST",
        body: JSON.stringify({ r2_keys: r2Keys }),
      },
    ),

  // Upload a file via XHR so the UI can report upload-byte progress —
  // the Worker runs extraction (OCR / PDF text) after the full body
  // arrives, so once progress hits 100% we're effectively in the
  // "extracting" phase until the response lands. The onUploadProgress
  // callback receives 0–100. Network/HTTP errors are mapped to the
  // same shape `request()` produces (401 triggers /welcome redirect).
  uploadFile: async (
    caseId: string,
    file: File,
    onUploadProgress?: (percent: number) => void,
  ): Promise<FileUploadResult> => {
    const token = _getToken ? await _getToken() : null;
    const form = new FormData();
    form.append("file", file);
    return new Promise<FileUploadResult>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_BASE}/api/cases/${caseId}/files`);
      xhr.withCredentials = true;
      // Hard ceiling on the whole request — measured from send() to the
      // response landing, so it spans both the byte upload AND the
      // server-side extraction (OCR / PDF text / AI metadata / Whisper
      // transcription), which is the slow part. A genuine 25MB upload on
      // a slow link plus long-audio transcription can take a couple of
      // minutes; 5 minutes leaves comfortable headroom for legitimate
      // uploads while still bounding a stuck request so it can never spin
      // forever (the bug this guards against had no timeout at all).
      xhr.timeout = 5 * 60 * 1000;
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      for (const [k, v] of Object.entries(aiPrefHeaders())) {
        xhr.setRequestHeader(k, v);
      }
      xhr.upload.addEventListener("progress", (e) => {
        if (onUploadProgress && e.lengthComputable) {
          onUploadProgress(
            Math.min(100, Math.round((e.loaded / e.total) * 100)),
          );
        }
      });
      xhr.addEventListener("load", () => {
        if (xhr.status === 401) {
          if (window.location.pathname !== "/welcome") {
            window.location.href = "/welcome";
          }
          reject(new Error("Unauthorized"));
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as FileUploadResult);
          } catch (err) {
            reject(new Error(`upload_response_parse_failed: ${(err as Error).message}`));
          }
          return;
        }
        // Non-2xx: surface the server's reason AND the status code on the
        // row, never a silent spinner. handleOneFile() catches this and
        // flips the item to its "error" state with this message.
        let msg = `HTTP ${xhr.status}`;
        try {
          const body = JSON.parse(xhr.responseText) as { error?: string };
          if (body.error) msg = `${body.error} (HTTP ${xhr.status})`;
        } catch {
          /* non-JSON body — keep the bare HTTP status */
        }
        reject(new Error(msg));
      });
      xhr.addEventListener("error", () =>
        reject(new Error("upload_network_error")),
      );
      xhr.addEventListener("abort", () =>
        reject(new Error("upload_aborted")),
      );
      xhr.addEventListener("timeout", () =>
        reject(new Error("upload timed out — retry")),
      );
      xhr.send(form);
    });
  },

  ocrImage: async (caseId: string, blob: Blob): Promise<string> => {
    const form = new FormData();
    form.append("image", blob, "frame.png");
    const res = await request<{ text: string }>(
      `/api/cases/${caseId}/ocr`,
      { method: "POST", body: form },
    );
    return res.text;
  },

  interrogate: (
    caseId: string,
    messages: ChatMessage[],
    opts: {
      task?:
        | "interrogate_simple"
        | "interrogate_complex"
        | "draft_letter"
        | "draft_complaint";
      // Round 12: authoritative pre-computed ledger figures (totals,
      // durations, reconciliations) spliced into the prompt so the model
      // narrates them verbatim and never recomputes.
      ledger?: string;
    } = {},
  ) =>
    request<{
      reply: string;
      provider: "anthropic" | "ollama";
      model: string;
      chunked: { summarised_records: number; kept_verbatim: number } | null;
      estimated_tokens: number;
      validation?: ValidationResult | null;
    }>(`/api/cases/${caseId}/interrogate`, {
      method: "POST",
      body: JSON.stringify({
        messages,
        task: opts.task ?? "interrogate_complex",
        ...(opts.ledger ? { ledger: opts.ledger } : {}),
      }),
    }),

  // Focused Answer — deterministic term expansion + retrieval preview.
  // No AI, no usage charge. Pass `phrase` to expand it into a new concept
  // group, `groups` (the user's edited chips) to re-preview, or both to
  // append an expanded concept to the existing groups.
  focusedPrepare: (
    caseId: string,
    body: { phrase?: string; groups?: string[][] },
  ) =>
    request<FocusedPrepareResult>(`/api/cases/${caseId}/focused/prepare`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Focused Answer — AI over the PINNED record subset. The client pins the
  // set from /prepare and sends `recordIds` (+ per-record `collapseCounts`)
  // with EVERY question, incl. Q1, so the answered scope == the set shown in
  // the UI (FTS is bypassed). Follow-ups also send the prior `messages` so
  // references resolve; grounding stays pinned to the records. A 413
  // "too_large" means narrow it; nothing is ever summarized.
  focusedAnswer: (
    caseId: string,
    body: {
      recordIds: string[];
      collapseCounts?: Record<string, number>;
      messages: ChatMessage[];
    },
  ) =>
    request<FocusedAnswerResult>(`/api/cases/${caseId}/focused/answer`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Focused "answer across all groups" — MAP: structured findings over ONE
  // group. Client fans these out (concurrency-capped) then posts them to
  // reduce. One AI call, usage-gated.
  focusedMap: (
    caseId: string,
    body: {
      recordIds: string[];
      collapseCounts?: Record<string, number>;
      question: string;
    },
  ) =>
    request<FocusedMapResult>(`/api/cases/${caseId}/focused/map`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Focused "answer across all groups" — REDUCE: consolidate all groups'
  // findings into one spanning, cited answer (pairs requests↔responses across
  // groups). One AI call. 413 findings_too_large = narrow the search.
  focusedReduce: (
    caseId: string,
    body: {
      question: string;
      groupFindings: Array<{
        group_index: number;
        from_date: string;
        to_date: string;
        findings: FocusedFinding[];
        failed: boolean;
      }>;
      unverifiedQuotes?: number;
      unverifiedDates?: number;
      quoteFailRecIds?: string[];
      dateFailRecIds?: string[];
      mapTruncated?: boolean;
      fingerprint?: string;
      matchedCount?: number;
    },
  ) =>
    request<FocusedReduceResult>(`/api/cases/${caseId}/focused/reduce`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // The case's most recent persisted cross-group answer (restore on load).
  focusedResult: (caseId: string) =>
    request<{ result: StoredReduceResult | null }>(
      `/api/cases/${caseId}/focused/result`,
    ),

  // Amount-category overrides (Round 12): the user's corrected bucket
  // (cost / recovery / coverage_term / rate) for a verified amount,
  // content-keyed so it survives re-extraction. bucket=null clears it.
  getAmountCategories: (caseId: string) =>
    request<{
      overrides: Array<{
        fact_key: string;
        bucket: string;
        status?: string | null;
        amount_cents_override?: number | null;
      }>;
    }>(`/api/cases/${caseId}/amount-categories`),
  setAmountCategory: (caseId: string, factKey: string, bucket: string | null) =>
    request<{ ok: true; bucket: string | null }>(
      `/api/cases/${caseId}/amount-categories`,
      { method: "PUT", body: JSON.stringify({ fact_key: factKey, bucket }) },
    ),
  // Merge-upsert: send only the fields you're changing (status / amount).
  setAmountOverride: (
    caseId: string,
    factKey: string,
    patch: { bucket?: string | null; status?: string | null; amount_cents_override?: number | null },
  ) =>
    request<{ ok: true }>(`/api/cases/${caseId}/amount-categories`, {
      method: "PUT",
      body: JSON.stringify({ fact_key: factKey, ...patch }),
    }),

  // TBD items (manual cost placeholders + graduation links).
  getTbdItems: (caseId: string) =>
    request<{
      items: Array<{
        key: string;
        description: string;
        party: string | null;
        status: string | null;
        amount_cents: number | null;
        resolved_fact_key: string | null;
      }>;
    }>(`/api/cases/${caseId}/tbd-items`),
  putTbdItem: (
    caseId: string,
    item: {
      key: string;
      description: string;
      party?: string | null;
      status?: string | null;
      amount_cents?: number | null;
      resolved_fact_key?: string | null;
    },
  ) =>
    request<{ ok: true }>(`/api/cases/${caseId}/tbd-items`, {
      method: "PUT",
      body: JSON.stringify(item),
    }),
  deleteTbdItem: (caseId: string, key: string) =>
    request<{ ok: true }>(
      `/api/cases/${caseId}/tbd-items/${encodeURIComponent(key)}`,
      { method: "DELETE" },
    ),

  // Merge / keep-separate decisions for ambiguous duplicate pairs + graduation.
  getMergeLinks: (caseId: string) =>
    request<{ links: Array<{ pair_key: string; decision: string }> }>(
      `/api/cases/${caseId}/merge-links`,
    ),
  setMergeLink: (caseId: string, pairKey: string, decision: "merge" | "separate") =>
    request<{ ok: true }>(`/api/cases/${caseId}/merge-links`, {
      method: "PUT",
      body: JSON.stringify({ pair_key: pairKey, decision }),
    }),

  // --- Models + Usage ---
  listModels: () =>
    request<{
      ollama_available: boolean;
      local: Array<{
        name: string;
        sizeGb: number;
        bestFor: string;
        kind: "local";
        pulled: boolean;
        size_bytes: number | null;
      }>;
      cloud: Array<{
        name: string;
        kind: "cloud";
        bestFor: string;
        configured: boolean;
      }>;
    }>("/api/models"),

  modelsStatus: () =>
    request<{
      ollama_available: boolean;
      pulled: Array<{ name: string; size: number }>;
    }>("/api/models/status"),

  pullModel: async (
    model: string,
    onProgress: (line: string) => void,
  ): Promise<void> => {
    // pullModel streams ND-JSON progress so it can't go through the
    // standard request() helper (which parses JSON). Resolve the Clerk
    // token the same way request() does so the auth behaviour is
    // identical — same provider, same null-is-anonymous semantics.
    const token = _getToken ? await _getToken() : null;
    const res = await fetch(`${API_BASE}/api/models/pull`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ model }),
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body.error) msg = body.error;
      } catch {}
      throw new Error(msg);
    }
    if (!res.body) return;
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf("\n")) !== -1) {
        const line = buf.slice(0, idx).trim();
        buf = buf.slice(idx + 1);
        if (line) onProgress(line);
      }
    }
  },

  // Landing-page waitlist signup. No auth headers — the endpoint is
  // public, so we bypass the normal `request()` wrapper (which attaches
  // the bearer token and would 401 for anonymous visitors on hosts
  // without it).
  joinWaitlist: async (email: string, plan: "personal" | "business") => {
    const res = await fetch(`${API_BASE}/waitlist`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, plan }),
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body.error) msg = body.error;
      } catch {}
      throw new Error(msg);
    }
    return (await res.json()) as { ok: true };
  },

  usage: () =>
    request<{
      all_time: { n: number; in_tok: number; out_tok: number; cost: number };
      this_month: {
        n: number;
        in_tok: number;
        out_tok: number;
        cost: number;
      };
      month_projection_usd: number;
      savings_usd: number;
      breakdown: Array<{
        task_type: string;
        provider: string;
        n: number;
        in_tok: number;
        out_tok: number;
        cost: number;
      }>;
    }>("/api/usage"),

  // Consumer-facing monthly usage meter. Deliberately separate from usage()
  // above so it never leaks cost numbers or token counts into the UI.
  // `limit` and `warn_threshold` are null for BYOK users — no ceiling
  // applies since they pay their provider directly.
  aiUsageMeter: () =>
    request<{
      ai_calls: number;
      generation_calls?: number;
      auditor_calls?: number;
      limit: number | null;
      warn_threshold: number | null;
      reset_at: string; // ISO timestamp of the next 1st-of-month reset
      byo_key_active: boolean;
      byo_key_set_at: string | null;
      byo_provider: ByoProvider | null;
      byo_model: string | null;
      allowed: boolean; // false = over quota, next AI call will 429
    }>("/api/ai/usage-meter"),

  getApiKey: () =>
    request<{
      set: boolean;
      set_at: string | null;
      provider: ByoProvider;
      model: string | null;
    }>("/api/ai/api-key"),

  setApiKey: (opts: {
    key: string;
    provider: ByoProvider;
    model?: string | null;
  }) =>
    request<{
      set: true;
      set_at: string;
      provider: ByoProvider;
      model: string | null;
    }>("/api/ai/api-key", {
      method: "PUT",
      body: JSON.stringify({
        key: opts.key,
        provider: opts.provider,
        model: opts.model ?? null,
      }),
    }),

  deleteApiKey: () =>
    request<{
      set: false;
      set_at: null;
      provider: ByoProvider;
      model: null;
    }>("/api/ai/api-key", {
      method: "DELETE",
    }),

  // Billing / subscription. All three go through the Clerk-auth pipe;
  // checkout and portal return a Stripe-hosted URL the caller should
  // redirect the browser to (`window.location.href = url`).
  billingCheckout: (plan: "monthly" | "annual" = "monthly") =>
    request<{ url: string }>("/api/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ plan }),
    }),
  billingPortal: () =>
    request<{ url: string }>("/api/billing/portal", { method: "POST" }),
  billingStatus: () =>
    request<{
      plan: string;
      trial_ends_at: string | null;
      days_remaining: number | null;
      has_subscription: boolean;
      cancel_at_period_end: boolean;
      current_period_end: string | null;
    }>("/api/billing/status"),

  // Stripe-backed extras used by Settings → Plan & Billing. All three
  // mutate the user's live subscription. The server persists the
  // relevant fields back to D1 so the UI state stays in sync without
  // a round-trip to /api/me afterwards.
  billingSubscriptionDetails: () =>
    request<{
      cadence: "monthly" | "annual" | null;
      current_period_end: string | null;
      cancel_at_period_end: boolean;
      status: string | null;
    }>("/api/billing/subscription-details"),
  billingCancel: () =>
    request<{
      cancel_at_period_end: boolean;
      current_period_end: string | null;
    }>("/api/billing/cancel", { method: "POST" }),
  billingReactivate: () =>
    request<{
      cancel_at_period_end: boolean;
      current_period_end: string | null;
    }>("/api/billing/reactivate", { method: "POST" }),
  billingSwitchPlan: (plan: "monthly" | "annual") =>
    request<{
      cadence: "monthly" | "annual" | null;
      current_period_end: string | null;
      cancel_at_period_end: boolean;
    }>("/api/billing/switch-plan", {
      method: "POST",
      body: JSON.stringify({ plan }),
    }),

  // Volumes — see CaseThread-Volumes-Design.md. Single-volume cases
  // render with zero volume UI; these endpoints still work but the
  // frontend just doesn't show anything until the case has >1 volume
  // or the current volume crosses the prompt threshold.
  listVolumes: (caseId: string) =>
    request<{ volumes: CaseVolume[] }>(
      `/api/cases/${caseId}/volumes`,
    ),

  // Maintain safety net: recompute every volume's denormalized counters
  // (record_count, estimated_tokens, first/last_record_date) from the
  // actual records. Fixes counters that drifted from deletions.
  recomputeVolumeStats: (caseId: string) =>
    request<{ recomputed: number; volumes: CaseVolume[] }>(
      `/api/cases/${caseId}/volumes/recompute-stats`,
      { method: "POST" },
    ),

  closeVolume: (caseId: string) =>
    request<{
      closed: CaseVolume & { summary: string | null; key_facts: KeyFact[] };
      next: CaseVolume;
    }>(`/api/cases/${caseId}/volumes/close`, { method: "POST" }),

  updateVolumeTitle: (caseId: string, volumeId: string, title: string) =>
    request<{ volume: CaseVolume }>(
      `/api/cases/${caseId}/volumes/${volumeId}`,
      {
        method: "PATCH",
        body: JSON.stringify({ title }),
      },
    ),

  volumeSummary: (caseId: string, volumeId: string) =>
    request<VolumeSummaryPayload>(
      `/api/cases/${caseId}/volumes/${volumeId}/summary`,
    ),

  // Re-run the volume's summary/title/key-facts pipeline on an
  // already-closed volume. One AI call; gated by the same plan +
  // usage + rate-limit stack as volume close. Returns the refreshed
  // payload so callers can drop it straight into state.
  regenerateVolumeSummary: (caseId: string, volumeId: string) =>
    request<VolumeSummaryPayload>(
      `/api/cases/${caseId}/volumes/${volumeId}/summary/regenerate`,
      { method: "POST" },
    ),

  // Drill-back interrogate — same request shape as `interrogate()`
  // but the target is a specific (usually closed) volume. The
  // backend loads that volume's records verbatim + other closed
  // volumes as summaries.
  interrogateVolume: (
    caseId: string,
    volumeId: string,
    messages: ChatMessage[],
    opts: {
      task?: "interrogate_simple" | "interrogate_complex";
    } = {},
  ) =>
    request<{
      reply: string;
      provider: "anthropic" | "ollama";
      model: string;
      chunked: { summarised_records: number; kept_verbatim: number } | null;
      estimated_tokens: number;
      volume_id: string;
    }>(`/api/cases/${caseId}/volumes/${volumeId}/interrogate`, {
      method: "POST",
      body: JSON.stringify({
        messages,
        task: opts.task ?? "interrogate_complex",
      }),
    }),

  // Batch deterministic extraction audit — re-audits every record in the
  // case and returns a summary. Zero AI cost.
  runAudit: (caseId: string) =>
    request<{
      audited: number;
      verified: number;
      flagged: number;
      flagged_records: {
        id: string;
        record_date: string;
        subject: string | null;
        issues: string[];
      }[];
    }>(`/api/cases/${caseId}/audit`, { method: "POST" }),

  // Persist per-finding validation actions (dismiss/accept/flag) for a
  // cached AI artefact. `target` is summary | timeline | costs | volume.
  saveValidationActions: (
    caseId: string,
    target: "summary" | "timeline" | "costs" | "volume",
    actions: FindingAction[],
    volumeId?: string,
  ) =>
    request<{ ok: true }>(`/api/cases/${caseId}/validation/actions`, {
      method: "POST",
      body: JSON.stringify({ target, volume_id: volumeId, actions }),
    }),

  // --- Verified facts (deterministic overview / timeline) ---
  getCaseFacts: (caseId: string) =>
    request<{ facts: RecordFact[] }>(`/api/cases/${caseId}/facts`),
  getRecordFacts: (recordId: string) =>
    request<{ facts: RecordFact[] }>(`/api/records/${recordId}/facts`),
  // Deterministic participation-evidence windows (signature tails + contact
  // windows incl. attachment text) for the parties-list gate.
  getParticipationEvidence: (caseId: string) =>
    request<{ windows: Array<{ record_id: string; text: string }> }>(
      `/api/cases/${caseId}/participation-evidence`,
    ),
  // Deterministic source resolution (which record/attachment a fact's quote
  // lives in) + verbatim contact collection for the reimbursement summary's
  // source-verification round-trip. No AI — substring resolution only.
  getSummarySources: (caseId: string) =>
    request<{
      sources: Array<{
        fact_id: string;
        record_id: string;
        attachment_id: string | null;
      }>;
      contacts: Array<{
        type: "email" | "phone";
        value: string;
        record_id: string;
        attachment_id: string | null;
        quote: string;
      }>;
    }>(`/api/cases/${caseId}/summary-sources`),
  // Expense-statement curation overrides (content-keyed, like commitment
  // resolutions). Round 9: rows also carry a user note + estimated flag;
  // the PUT writes the full row state.
  getExpenseOverrides: (caseId: string) =>
    request<{
      overrides: Array<{
        fact_key: string;
        included: boolean;
        note: string | null;
        estimated: boolean;
      }>;
    }>(`/api/cases/${caseId}/expense-overrides`),
  setExpenseOverride: (
    caseId: string,
    factKey: string,
    state: { included: boolean; note?: string | null; estimated?: boolean },
  ) =>
    request<{ ok: true; included: boolean }>(
      `/api/cases/${caseId}/expense-overrides`,
      {
        method: "PUT",
        body: JSON.stringify({
          fact_key: factKey,
          included: state.included,
          note: state.note ?? null,
          estimated: state.estimated ?? false,
        }),
      },
    ),
  getCommitmentResolutions: (caseId: string) =>
    request<{ keys: string[] }>(`/api/cases/${caseId}/commitment-resolutions`),
  setCommitmentResolution: (caseId: string, factKey: string, resolved: boolean) =>
    request<{ ok: true; resolved: boolean }>(
      `/api/cases/${caseId}/commitment-resolutions`,
      { method: "PUT", body: JSON.stringify({ fact_key: factKey, resolved }) },
    ),
  extractRecordFacts: (recordId: string) =>
    request<{ stored: number; discarded: number; facts: RecordFact[] }>(
      `/api/records/${recordId}/extract-facts`,
      { method: "POST" },
    ),
  deleteRecordFacts: (recordId: string) =>
    request<{ ok: true }>(`/api/records/${recordId}/facts`, {
      method: "DELETE",
    }),

  // Chunked, client-driven batch fact extraction with SSE progress.
  // A single Worker invocation
  // is capped at ~50 subrequests (~25 records), so we process the case in
  // chunks across multiple invocations — each chunk gets a fresh budget.
  // 2s pacing between chunks keeps us under Anthropic's rate limit too.
  // Chunked + client-driven (the ≤24-record chunk keeps each Worker invocation
  // under the subrequest cap). Each chunk streams over SSE so onProgress fires
  // PER RECORD — the UI/script counter advances continuously and a stall on one
  // record is visible (no progress event) rather than a frozen per-chunk count.
  extractAllFacts: async (
    caseId: string,
    onProgress?: (p: { completed: number; total: number }) => void,
    // Injectable for testing (the chunk endpoint is idempotent — it REPLACES a
    // record's facts — so a chunk can be safely retried).
    streamChunk: typeof streamExtractChunk = streamExtractChunk,
  ): Promise<{
    total: number;
    stored: number;
    facts: RecordFact[];
    flagged: ExtractionDropFlag[];
    failed: string[];
    // True when a chunk ultimately failed after retries and the run stopped
    // before processing everything — surfaced as partial, never a bare reject.
    partial: boolean;
    failedAtStart: number | null;
  }> => {
    const CHUNK = 20;
    const MAX_CHUNK_RETRIES = 2; // 3 attempts total per chunk
    let start = 0;
    let total = 0;
    let stored = 0;
    let partial = false;
    let failedAtStart: number | null = null;
    const flagged: ExtractionDropFlag[] = [];
    const failed: string[] = [];
    const facts = async () =>
      (await request<{ facts: RecordFact[] }>(`/api/cases/${caseId}/facts`)).facts;

    // Guard against a runaway loop; 100 chunks × 20 = 2000 records max.
    for (let guard = 0; guard < 100; guard++) {
      console.info(
        `[extract-all-facts] → streaming chunk start=${start} size=${CHUNK}`,
      );
      // A chunk that ends without a `done` event (stream cut / subrequest
      // limit / transient HTTP) is retried; re-running it just re-replaces the
      // same records' facts. After the retries are exhausted we STOP with a
      // partial result rather than rejecting the whole multi-chunk run.
      let r: ExtractChunkDone | null = null;
      for (let attempt = 0; attempt <= MAX_CHUNK_RETRIES; attempt++) {
        try {
          r = await streamChunk(caseId, start, CHUNK, (completed, t) => {
            total = t;
            onProgress?.({ completed, total: t });
          });
          break;
        } catch (e) {
          console.warn(
            `[extract-all-facts] chunk start=${start} attempt ${attempt + 1} failed: ${(e as Error).message}`,
          );
          if (attempt < MAX_CHUNK_RETRIES) {
            await new Promise<void>((res) => setTimeout(res, 1500));
          }
        }
      }
      if (!r) {
        // Ultimate failure for this chunk — partial completion, no reject.
        partial = true;
        failedAtStart = start;
        break;
      }
      console.info(
        `[extract-all-facts] ← processed=${r.processed} stored=${r.stored} failed=${r.failed.length} flagged=${r.flagged?.length ?? 0} next_start=${r.next_start} total=${r.total} done=${r.done}`,
      );
      total = r.total;
      stored += r.stored;
      if (r.flagged) flagged.push(...r.flagged);
      failed.push(...r.failed);
      onProgress?.({ completed: r.next_start, total });
      if (r.done) break;
      start = r.next_start;
      // Pace between chunks.
      await new Promise<void>((res) => setTimeout(res, 2000));
    }
    return { total, stored, facts: await facts(), flagged, failed, partial, failedAtStart };
  },
};

// The /images route streams any R2 blob for the case (not just images).
// It stays at that path for backward compat and to avoid churning the Worker.
export function fileUrl(caseId: string, r2Key: string): string {
  const prefix = `cases/${caseId}/`;
  const rest = r2Key.startsWith(prefix) ? r2Key.slice(prefix.length) : r2Key;
  return `${API_BASE}/images/${caseId}/${rest}`;
}

// Back-compat alias; prefer fileUrl.
export const imageUrl = fileUrl;
